import * as React from 'react'
import { createPortal } from 'react-dom'
import { Mascot } from '@repo/studio-features-3d-mascot/mascot'
import { useAidaChat } from '../../context/aida-chat-context'
import { useCollapsingMascot, useMascotSlotMotion } from '../../hooks/use-collapsing-mascot'
import { AidaPaneActiveContext } from '../../utils/pane-active'
import { getAidaPortraitUrl } from '../../utils/lfs-url'
import { tw } from 'utils/classnames'

interface HeaderAnchors {
  headerAnchor: HTMLDivElement | null
  setHeaderAnchor: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
  slotOpen: boolean
  setSlotOpen: React.Dispatch<React.SetStateAction<boolean>>
}

interface ThreadSetters {
  setThreadAnchor: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
  setScroller: React.Dispatch<React.SetStateAction<HTMLElement | null>>
  setLiftLayer: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>
}

const MascotHeaderAnchorContext = React.createContext<HeaderAnchors | null>(null)
const MascotThreadSetterContext = React.createContext<ThreadSetters | null>(null)

function useHeaderAnchorController(): HeaderAnchors {
  const [headerAnchor, setHeaderAnchor] = React.useState<HTMLDivElement | null>(null)
  const [slotOpen, setSlotOpen] = React.useState(false)
  return React.useMemo(
    () => ({ headerAnchor, setHeaderAnchor, slotOpen, setSlotOpen }),
    [headerAnchor, slotOpen],
  )
}

export function MascotAnchorProvider({ children }: { children: React.ReactNode }) {
  const parent = React.useContext(MascotHeaderAnchorContext)
  const local = useHeaderAnchorController()
  if (parent) {
    return children
  }
  return (
    <MascotHeaderAnchorContext.Provider value={local}>
      {children}
    </MascotHeaderAnchorContext.Provider>
  )
}

function useHeaderAnchors() {
  const anchors = React.useContext(MascotHeaderAnchorContext)
  if (!anchors) {
    throw new Error('Mascot header anchor must be registered inside MascotAnchorProvider')
  }
  return anchors
}

export function useHeaderMascotSlotOpen() {
  return React.useContext(MascotHeaderAnchorContext)?.slotOpen ?? false
}

function useThreadSetters() {
  const setters = React.useContext(MascotThreadSetterContext)
  if (!setters) {
    throw new Error('Mascot thread anchor must be registered inside MascotPresence')
  }
  return setters
}

export function MascotHeaderAnchor() {
  const { setHeaderAnchor, slotOpen } = useHeaderAnchors()
  const nodeRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node) {
      return
    }
    setHeaderAnchor(node)
    return () => {
      setHeaderAnchor(current => (current === node ? null : current))
    }
  }, [setHeaderAnchor])

  return (
    <div
      ref={nodeRef}
      className={tw(
        'relative h-8 min-w-0 shrink-0 overflow-hidden transition-[width] duration-200 ease-in-out motion-reduce:transition-none',
        slotOpen ? 'pointer-events-auto w-8' : 'pointer-events-none w-0',
      )}
    />
  )
}

export function MascotThreadAnchor({ className }: { className?: string }) {
  const { setThreadAnchor } = useThreadSetters()
  const nodeRef = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const node = nodeRef.current
    if (!node) {
      return
    }
    setThreadAnchor(node)
    return () => {
      setThreadAnchor(current => (current === node ? null : current))
    }
  }, [setThreadAnchor])

  return <div ref={nodeRef} className={className} />
}

export function useRegisterMascotScroller(
  scrollerRef: React.RefObject<HTMLElement | null>,
  liftRef: React.RefObject<HTMLDivElement | null>,
) {
  const { setScroller, setLiftLayer } = useThreadSetters()

  React.useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const lift = liftRef.current
    if (scroller) {
      setScroller(scroller)
    }
    if (lift) {
      setLiftLayer(lift)
    }
    return () => {
      if (scroller) {
        setScroller(current => (current === scroller ? null : current))
      }
      if (lift) {
        setLiftLayer(current => (current === lift ? null : current))
      }
    }
  }, [liftRef, scrollerRef, setLiftLayer, setScroller])
}

export function MascotPresence({
  modelUrl,
  children,
}: {
  modelUrl: string
  children: React.ReactNode
}) {
  return (
    <MascotAnchorProvider>
      <MascotThreadHost modelUrl={modelUrl}>{children}</MascotThreadHost>
    </MascotAnchorProvider>
  )
}

function MascotThreadHost({ modelUrl, children }: { modelUrl: string; children: React.ReactNode }) {
  const [threadAnchor, setThreadAnchor] = React.useState<HTMLDivElement | null>(null)
  const [scroller, setScroller] = React.useState<HTMLElement | null>(null)
  const [liftLayer, setLiftLayer] = React.useState<HTMLDivElement | null>(null)
  const setters = React.useMemo(() => ({ setThreadAnchor, setScroller, setLiftLayer }), [])

  return (
    <MascotThreadSetterContext.Provider value={setters}>
      {children}
      <MascotCanvas
        modelUrl={modelUrl}
        threadAnchor={threadAnchor}
        scroller={scroller}
        liftLayer={liftLayer}
      />
    </MascotThreadSetterContext.Provider>
  )
}

function MascotCanvas({
  modelUrl,
  threadAnchor,
  scroller,
  liftLayer,
}: {
  modelUrl: string
  threadAnchor: HTMLDivElement | null
  scroller: HTMLElement | null
  liftLayer: HTMLDivElement | null
}) {
  const { mascotRef, messages, conversationsReady, historyReady } = useAidaChat()
  const paneActive = React.useContext(AidaPaneActiveContext)
  const headerAnchors = React.useContext(MascotHeaderAnchorContext)
  const headerAnchor = headerAnchors?.headerAnchor ?? null
  const setSlotOpen = headerAnchors?.setSlotOpen
  const [park, setPark] = React.useState<HTMLDivElement | null>(null)
  const [slot] = React.useState(() => document.createElement('div'))
  const frameRef = React.useRef<HTMLDivElement>(null)
  const onCollapsed = React.useRef<(collapsed: boolean) => void>(() => {})
  onCollapsed.current = next => {
    setSlotOpen?.(next && headerAnchor !== null)
  }
  const collapsed = useCollapsingMascot(scroller, threadAnchor, slot, liftLayer, onCollapsed)
  const empty = conversationsReady && historyReady && messages.length === 0
  const inHeader = collapsed && headerAnchor !== null
  const trackingLayer = liftLayer && threadAnchor ? liftLayer : null
  const host = inHeader ? headerAnchor : (trackingLayer ?? threadAnchor ?? park)
  const onScreen = paneActive && host !== null && host !== park && !(collapsed && !inHeader)
  useMascotSlotMotion(frameRef, slot, host, trackingLayer, threadAnchor, inHeader, empty)
  React.useLayoutEffect(() => {
    setSlotOpen?.(inHeader)
  }, [inHeader, setSlotOpen])
  React.useEffect(() => {
    return () => {
      slot.remove()
    }
  }, [slot])

  const scrollToTop = () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    scroller?.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' })
  }

  const frame = (
    <div className="pointer-events-auto size-full">
      <div ref={frameRef} className={inHeader ? 'size-8 shrink-0' : 'size-full'}>
        <Mascot
          ref={mascotRef}
          modelUrl={modelUrl}
          facing="right"
          active={onScreen}
          placeholderSrc={getAidaPortraitUrl()}
          label={inHeader ? 'Scroll to top' : 'Mascot'}
          className="size-full"
          onActivate={inHeader ? scrollToTop : undefined}
        />
      </div>
    </div>
  )

  return (
    <>
      <div ref={setPark} className="hidden" />
      {createPortal(frame, slot)}
    </>
  )
}
